<template>
  <Alert class="mt-8">
    <template #title>À propos de la rémunération réelle</template>
    <p>
      La rémunération réelle correspond à la rémunération magasin nette de résiliations et d’impayés. Celle-ci est
      versée à la date de versement sélectionnée et est calculée sur la base des ventes réalisées à M-2.
    </p>
  </Alert>
  <div
    class="mt-8 bg-white shadow-sm sm:rounded-t-md py-6 px-4 lg:px-6 gap-x-6 flex flex-col sm:flex-row justify-between mx-auto"
  >
    <div class="pt-2 sm:w-64">
      <p class="block text-sm leading-6 text-gray-900 sm:pt-1.5 font-semibold">Date de versement</p>
      <MonthPicker
        class="text-sm w-full rounded-md"
        :placeholder="$t('Select a time range')"
        :model-value="dateRange"
        @update:modelValue="dateRangeUpdated"
      />
    </div>
    <div class="sm:w-64">
      <MultiSelect
        id="store-selector"
        label="Magasins"
        :options="storeOptions"
        v-model="selectedStores"
        placeholder="Choisissez un ou plusieurs magasins"
      />
    </div>
  </div>
  <div class="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
    <div class="bg-white shadow-sm rounded-md py-4 px-4 lg:px-6">
      <h2 class="text-lg font-semibold">Rémunération réelle</h2>
      <span v-if="isPresent(selectedRemunerations)" class="text-md font-semibold text-green-500">
        {{ formatPrice(selectedTotalRemuneration) }}
      </span>
      <span v-else class="text-md font-semibold text-green-500">
        {{ formatPrice(totalRemuneration) }}
      </span>
      <span class="text-sm" v-if="isPresent(selectedRemunerations)">
        (Compa. Réseau:
        <span :class="comparisonClass(totalAverageRemuneration, remunerationAverage)">
          {{ comparisonPercentage(totalAverageRemuneration, remunerationAverage) }}%
        </span>
        )
      </span>
      <div v-if="!analytics?.remunerations?.length" class="text-gray-500 text-center py-4">
        Aucune donnée disponible pour la période sélectionnée. Veuillez sélectionner une autre période.
      </div>
      <Table v-else :columns="remunerationColumns" :data="formattedRemunerations" :isRowClickable="false"></Table>
    </div>

    <div class="bg-white shadow-sm rounded-md py-4 px-4 lg:px-6">
      <h2 class="text-lg font-semibold">Rémunération moyenne par contrat</h2>
      <span v-if="isPresent(selectedRemunerationsByContract)" class="text-md font-semibold text-green-500">
        {{ formatPrice(selectedAverageRemunerationsByContract) }}
      </span>
      <span v-else class="text-md font-semibold text-green-500">
        {{ formatPrice(remunerationsByContractAverage) }}
      </span>
      <span class="text-sm" v-if="isPresent(selectedRemunerationsByContract)">
        (Compa. Réseau:
        <span :class="comparisonClass(selectedAverageRemunerationsByContract, remunerationsByContractAverage)">
          {{ comparisonPercentage(selectedAverageRemunerationsByContract, remunerationsByContractAverage) }}%
        </span>
        )
      </span>
      <div v-if="!analytics?.remunerations?.length" class="text-gray-500 text-center py-4">
        Aucune donnée disponible pour la période sélectionnée. Veuillez sélectionner une autre période.
      </div>
      <Table
        v-else
        :columns="remunerationByContractColumns"
        :data="formattedRemunerationsByContract"
        :isRowClickable="false"
      ></Table>
    </div>

    <div class="bg-white shadow-sm rounded-md py-4 px-4 lg:px-6">
      <h2 class="text-lg font-semibold">Ventes réalisées</h2>
      <span v-if="isPresent(selectedSales)" class="text-md font-semibold text-green-500">
        {{ selectedTotalSales }}
      </span>
      <span v-else class="text-md font-semibold text-green-500">
        {{ totalSales }}
      </span>
      <span class="text-sm" v-if="isPresent(selectedSales)">
        (Compa. Réseau:
        <span :class="comparisonClass(totalAverageSales, salesAverage)">
          {{ comparisonPercentage(totalAverageSales, salesAverage) }}%
        </span>
        )
      </span>
      <div v-if="!analytics?.remunerations?.length" class="text-gray-500 text-center py-4">
        Aucune donnée disponible pour la période sélectionnée. Veuillez sélectionner une autre période.
      </div>
      <Table v-else :columns="salesColumns" :data="formattedSales" :isRowClickable="false"></Table>
    </div>
  </div>
</template>

<script>
import MultiSelect from "@/components/utils/MultiSelect.vue";
import AnalyticsService from "@/api/services/analytics.js";
import { MonthPicker, Alert, Table, formatPrice, toCamelCase, isPresent } from "@estaly/ui";
import { formatDate } from "@/utils/date_formatter";
import { mapActions } from "vuex";
import { startOfMonth, endOfMonth } from "date-fns";

export default {
  components: {
    MonthPicker,
    MultiSelect,
    Alert,
    Table,
  },
  data() {
    return {
      dateRange: [startOfMonth(new Date()), endOfMonth(new Date())],
      analytics: null,
      selectedStores: [],
      remunerationAverage: null,
      salesAverage: null,
      remunerationsByContractAverage: null,
      token: null,
    };
  },

  mounted() {
    const token = this.$route.query.token;
    if (!token) {
      this.notify({
        category: "simple",
        type: "error",
        title: "Un token est requis pour accéder à cette page.",
      });
      return;
    }

    this.token = token;
    this.withLoader(this.fetchAnalytics);
  },

  computed: {
    remunerationColumns() {
      return [
        { label: "Position", field: "position" },
        { label: "Magasin", field: "entityName" },
        { label: "M", field: "amount" },
      ];
    },
    formattedRemunerations() {
      return (
        this.analytics?.remunerations?.map((item, index) => ({
          position: index + 1,
          entityName: item.entityName,
          amount: this.formatPrice(item.amount),
          rowClass: this.selectedStores.includes(item.entityName) ? "bg-green-200" : "",
        })) || []
      );
    },
    salesColumns() {
      return [
        { label: "Position", field: "position" },
        { label: "Magasin", field: "entityName" },
        { label: "M", field: "sales" },
      ];
    },
    formattedSales() {
      return (
        this.analytics?.sales?.map((item, index) => ({
          position: index + 1,
          entityName: item.entityName,
          sales: item.sales,
          rowClass: this.selectedStores.includes(item.entityName) ? "bg-green-200" : "",
        })) || []
      );
    },
    remunerationByContractColumns() {
      return [
        { label: "Position", field: "position" },
        { label: "Magasin", field: "entityName" },
        { label: "M", field: "amount" },
      ];
    },

    formattedRemunerationsByContract() {
      return (
        this.analytics?.remunerationsByContract?.map((item, index) => ({
          position: index + 1,
          entityName: item.entityName,
          amount: this.formatPrice(item.amount),
          rowClass: this.selectedStores.includes(item.entityName) ? "bg-green-200" : "",
        })) || []
      );
    },
    params() {
      return {
        startDate: formatDate(this.dateRange[0]),
        endDate: formatDate(this.dateRange[1]),
        token: this.token,
      };
    },
    storeOptions() {
      return this.analytics?.remunerations
        .map((item) => ({
          label: item.entityName,
          value: item.entityName,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    selectedRemunerations() {
      return this.analytics?.remunerations.filter((item) => this.selectedStores.includes(item.entityName));
    },
    selectedRemunerationsByContract() {
      return this.analytics?.remunerationsByContract.filter((item) => this.selectedStores.includes(item.entityName));
    },
    selectedSales() {
      return this.analytics?.sales.filter((item) => this.selectedStores.includes(item.entityName));
    },
    selectedTotalRemuneration() {
      return this.selectedRemunerations.reduce((sum, item) => sum + Number(item.amount), 0);
    },
    selectedTotalSales() {
      return this.selectedSales.reduce((sum, item) => sum + item.sales, 0);
    },
    selectedAverageRemunerationsByContract() {
      return (
        this.selectedRemunerationsByContract.reduce((sum, item) => sum + Number(item.amount), 0) /
        this.selectedRemunerationsByContract.length
      );
    },
    totalAverageRemuneration() {
      return (
        this.selectedRemunerations.reduce((sum, item) => sum + Number(item.amount), 0) /
        this.selectedRemunerations.length
      );
    },
    totalAverageSales() {
      return this.selectedSales.reduce((sum, item) => sum + item.sales, 0) / this.selectedSales.length;
    },
    totalRemuneration() {
      return this.analytics?.remunerations.reduce((sum, item) => sum + Number(item.amount), 0);
    },
    totalSales() {
      return this.analytics?.sales.reduce((sum, item) => sum + item.sales, 0);
    },
  },

  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("store", ["withLoader"]),
    formatPrice,
    isPresent,

    comparisonPercentage(value, average) {
      if (!value || !average) return 0;
      const percentage = ((value - average) / average) * 100;
      return percentage.toFixed(2);
    },

    comparisonClass(value, average) {
      if (!value || !average) return "text-gray-500";
      return value > average ? "text-green-500" : "text-red-500";
    },

    async fetchAnalytics() {
      let response = await AnalyticsService.computeOrganizationInvoicesAnalytics(this.params);
      if (response?.success) {
        const data = toCamelCase(response.data);

        this.analytics = data.analytics;
        this.remunerationAverage = this.analytics.remunerationAverage;
        this.salesAverage = this.analytics.salesAverage;
        this.remunerationsByContractAverage = this.analytics.remunerationsByContractAverage;
      }
    },

    dateRangeUpdated(dateRange) {
      if (!dateRange || dateRange.includes(null)) {
        this.dateRange = [];
      } else {
        this.dateRange = dateRange;
      }
      this.withLoader(this.fetchAnalytics);
    },
  },
};
</script>
